import { moneyFormatFromCents } from '../../../utilities/moneyFormat';
import { NON_CATEGORY_RULES } from './importCategoryRule.js';

/**
 * A rule read out as a sentence: what it matches, then what it does. Kept apart from the page so the
 * wording, which is the page's whole job, can be pinned in a test.
 */

/**
 * Each condition the rule has, in the order a row is described: where it came from, how the export
 * filed it, who it was paid to, what the statement said, how much. Empty when it has none.
 */
export function ruleConditions(rule) {
	const parts = [];
	if(rule.external_account) {
		parts.push(`Account ${rule.external_account}`);
	}
	if(rule.no_category) {
		parts.push('No category');
	} else if(rule.external_category) {
		parts.push(`Category ${rule.external_category}`);
	}
	if(rule.external_merchant) {
		parts.push(`Merchant ${rule.external_merchant}`);
	}
	if(rule.statement_contains) {
		parts.push(`Statement contains “${rule.statement_contains}”`);
	}
	if(rule.amount !== null && rule.amount !== undefined) {
		parts.push(`Amount ${moneyFormatFromCents(rule.amount)}`);
	}
	return parts;
}

export function ruleConditionsText(rule) {
	const parts = ruleConditions(rule);
	return parts.length ? parts.join(' · ') : 'Every row';
}

/**
 * Why a rule may not get the rows it looks meant for, from the listing: `unreachable`/`covered_by`
 * (an earlier rule takes every row it could) or `shadowed_by` (ids of earlier rules that take some
 * of them first), and `move_above`, the position that fixes it, which the server leaves null when the
 * move would leapfrog one of the rule's own carve-outs. Then there is the warning and no button.
 *
 * @return {{ text: string, moveTo: (number|null) }|null} Null when nothing is in its way.
 */
export function ruleShadow(rule, rules) {
	const positionOf = (id) => {
		const found = (rules || []).find((candidate) => candidate.id === id);
		return found && found.position < rule.position ? found.position : null;
	};
	const moveTo = rule.move_above === null || rule.move_above === undefined ? null : rule.move_above;
	const covering = rule.unreachable ? positionOf(rule.covered_by) : null;
	if(covering !== null) {
		return {
			text: `Rule ${covering} above already catches every row this one could, so it never applies.`,
			moveTo,
		};
	}
	const earlier = (rule.shadowed_by || []).map(positionOf).filter((position) => position !== null);
	if(!earlier.length) {
		return null;
	}
	return { text: `Rule ${Math.min(...earlier)} is checked first and can also catch these rows.`, moveTo };
}

/** What a non-category action does to the rows, in the words the page uses for it. */
export const ACTION_LABELS = {
	raw_only: 'Ignore (bank record only)',
	skip: 'Skip (not imported)',
	uncategorized: 'Uncategorized',
};

/**
 * What the rule turns its rows into. A category is named with its parent where it has one, as two
 * categories can share a name under different parents.
 */
export function ruleOutcomeText(rule, availableCategories) {
	let outcome;
	if(NON_CATEGORY_RULES.includes(rule.action)) {
		outcome = ACTION_LABELS[rule.action];
	} else {
		const category = (availableCategories || []).find((candidate) => candidate.id === rule.category_id);
		if(!category || rule.category_deleted) {
			outcome = 'A deleted category (its rows wait)';
		} else {
			outcome = category.parent_name ? `${category.parent_name} › ${category.name}` : category.name;
		}
	}
	/** Raw-only and skipped rows make no ledger transaction, so there is no sign to reverse. */
	if(rule.reverse_sign && (rule.action === 'assign' || rule.action === 'uncategorized')) {
		outcome += ', sign reversed';
	}
	return outcome;
}

/** The largest amount a rule can name: a staged row's amount is a signed 32-bit count of cents. */
export const MAX_RULE_CENTS = 2147483647;

/**
 * Dollars as typed into the editor, to the cents the server compares against a row's own amount.
 * Accepts "-900", "-$900.00", "−1,234.5" (a typographic minus too). Blank is no amount condition.
 *
 * @return {{ cents: (number|null), error: (string|null) }}
 */
export function dollarsToCents(text) {
	const cleaned = String(text === null || text === undefined ? '' : text)
		.trim()
		.replace(/[−–]/g, '-')
		.replace(/[$,\s]/g, '');
	if(cleaned === '') {
		return { cents: null, error: null };
	}
	if(!/^[-+]?(\d+(\.\d{0,2})?|\.\d{1,2})$/.test(cleaned)) {
		return { cents: null, error: 'Enter an amount like -900.00' };
	}
	const negative = cleaned.startsWith('-');
	const [whole, fraction = ''] = cleaned.replace(/^[-+]/, '').split('.');
	const cents = Number(whole || '0') * 100 + Number((fraction + '00').slice(0, 2));
	if(cents > MAX_RULE_CENTS) {
		return { cents: null, error: 'That amount is larger than any row can be.' };
	}
	return { cents: negative && cents !== 0 ? -cents : cents, error: null };
}

/** Cents back to what the editor shows: "-900.00", or '' for no amount. */
export function centsToDollars(cents) {
	if(cents === null || cents === undefined) {
		return '';
	}
	const negative = cents < 0;
	const absolute = Math.abs(cents);
	return `${negative ? '-' : ''}${Math.floor(absolute / 100)}.${String(absolute % 100).padStart(2, '0')}`;
}
